import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { getSupabase } from './supabase';

interface AuthValue {
  user: User | null;
  session: Session | null;
  authorized: boolean;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const supabase = getSupabase();
  const [session, setSession] = useState<Session | null>(null);
  const [authorized, setAuthorized] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const { data: listener } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      if (newSession?.user) {
        checkAuthorized(newSession.user.id).then(setAuthorized);
      } else {
        setAuthorized(false);
      }
    });

    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      if (data.session?.user) {
        checkAuthorized(data.session.user.id).then((ok) => {
          setAuthorized(ok);
          setLoading(false);
        });
      } else {
        setAuthorized(false);
        setLoading(false);
      }
    });

    return () => {
      listener.subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function checkAuthorized(userId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('viaypii_profiles')
        .select('id')
        .eq('id', userId)
        .maybeSingle();
      if (error) {
        console.error('Auth check error:', error.message);
        return false;
      }
      return !!data;
    } catch (e) {
      console.error('Auth check exception:', e);
      return false;
    }
  }

  async function signIn(email: string, password: string) {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        return { error: error.message };
      }
      if (!data.user) {
        return { error: 'Giriş yapılamadı.' };
      }
      const ok = await checkAuthorized(data.user.id);
      if (!ok) {
        await supabase.auth.signOut();
        return { error: 'Bu hesabın panele erişim yetkisi yok.' };
      }
      setAuthorized(true);
      return { error: null };
    } catch (e) {
      console.error('SignIn exception:', e);
      return { error: 'Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.' };
    }
  }

  async function signOut() {
    await supabase.auth.signOut();
    setAuthorized(false);
  }

  return (
    <AuthContext.Provider
      value={{ user: session?.user ?? null, session, authorized, loading, signIn, signOut }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}